import { renderConfirmationPlanogramProduct } from "@/components/modals/ConfirmationPlanogramProduct";
import {
  PlanogramViewer as ReusablePlanogramViewer,
  type PlanogramViewerHandle,
} from "@augmodo/reusable-components";
import { useAtlasPlanogram } from "@labeling-tools/api/hooks";
import { PlanogramViewer } from "@labeling-tools/ui-components/src/planogram-viewer/planogram-viewer.comp";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import { Loader } from "@mantine/core";
import { useEffect, useMemo, useRef } from "react";
import { useGate } from "statsig-react";

interface Props {
  planogramId: string;
  /** Product to frame on load (matched by product_id or placement id). */
  initialProductId?: string | null;
  /** Frame `initialProductId` without making it the active selection. */
  zoomOnlyInitialProduct?: boolean;
  /** Legacy-only: wider framing of `initialProductId` (the reusable viewer has one frame size). */
  wideInitialZoom?: boolean;
  /**
   * Explicit multi-selection (box-draw). When given, it alone drives the red
   * outlines — the clicked product is not highlighted on its own, matching the
   * legacy viewer.
   */
  selectedProductIds?: string[];
  /** Region to frame on load when there is no `initialProductId`. */
  zoomToProductIds?: string[];
}

/**
 * The confirmation task's planogram, behind the
 * `fe_enable_reusable_plano_viewer_confirmation` gate: the legacy
 * ui-components viewer, or the standalone one from
 * @augmodo/reusable-components. Both report product clicks through
 * useActivePlanoProductInfoStore, so consumers (PlanogramModal,
 * PlanogramOnlyWorkflow) don't know which is mounted.
 */
export const ConfirmationPlanogramViewer = ({
  planogramId,
  initialProductId,
  zoomOnlyInitialProduct = false,
  wideInitialZoom = false,
  selectedProductIds,
  zoomToProductIds,
}: Props) => {
  const { value: isEnableReusablePlanoViewer } = useGate(
    "fe_enable_reusable_plano_viewer_confirmation",
  );

  // The reusable viewer takes rows, not an id; the legacy one fetches for
  // itself (same query key, so nothing is fetched twice with the gate off).
  const { data: planogram, isLoading } = useAtlasPlanogram(
    isEnableReusablePlanoViewer ? planogramId : null,
  );

  // The clicked product, for the red outline. Legacy rule: an explicit
  // selection list replaces it rather than adding to it.
  const activeProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.product_id,
  );
  const activeProductIds = useMemo(
    () => selectedProductIds ?? (activeProductId ? [activeProductId] : []),
    [selectedProductIds, activeProductId],
  );

  // The reusable viewer takes camera moves as handle calls. Null while it isn't
  // mounted (gate off, planogram loading), so the calls below no-op there; the
  // `planogram` dep re-fires the effect once data arrives and it's back.
  const viewerRef = useRef<PlanogramViewerHandle>(null);
  const zoomToKey = zoomToProductIds?.join(",") ?? "";
  useEffect(() => {
    if (!planogram) return;
    const products = planogram.products ?? [];
    const target = initialProductId
      ? products.find(
          (p) => p.id === initialProductId || p.product_id === initialProductId,
        )
      : undefined;
    if (target) {
      viewerRef.current?.zoomToProducts([target.product_id]);
      if (zoomOnlyInitialProduct) return;
      // Make it the active selection — deferred a tick, because the owners
      // (PlanogramOnlyWorkflow) reset the store in their own effects, which
      // React runs AFTER this child effect in the same commit: a synchronous
      // write here would be wiped whenever the planogram data comes from
      // cache. The legacy viewer deferred for the same reason (450ms behind
      // its zoom animation); a timeout is the ordering that matters. The
      // clobber guard runs at fire time: if the labeler clicked something
      // in the meantime, their pick wins, as in the legacy viewer.
      const timeoutId = setTimeout(() => {
        const store = useActivePlanoProductInfoStore.getState();
        if (store.productBoxInfo.planogram_product_id) return;
        store.setActiveLabelBoxInfo({
          left: 0,
          top: 0,
          planogram_product_id: target.id,
          product_id: target.product_id,
        });
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    if (!initialProductId && zoomToProductIds?.length) {
      viewerRef.current?.zoomToProducts(zoomToProductIds);
    }
    // zoomToProductIds is keyed by content (callers pass fresh arrays).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    planogram,
    planogramId,
    initialProductId,
    zoomOnlyInitialProduct,
    zoomToKey,
  ]);

  // The store is a module-level singleton shared by every planogram flow in
  // the app. The owners reset it on their own transitions (modal close, task
  // change) — this covers the path they can't: unmounting without a close,
  // e.g. navigating to another task with the modal open. Applies to both gate
  // branches; the legacy viewer writes the same store.
  useEffect(() => () => useActivePlanoProductInfoStore.getState().reset(), []);

  if (!isEnableReusablePlanoViewer) {
    return (
      <PlanogramViewer
        planogram_id={planogramId}
        initialProductId={initialProductId}
        zoomOnlyInitialProduct={zoomOnlyInitialProduct}
        wideInitialZoom={wideInitialZoom}
        hidePopover={true}
        selectedProductIds={selectedProductIds}
        zoomToProductIds={zoomToProductIds}
      />
    );
  }

  // Same footprint as the legacy viewer's container.
  return (
    <div className="relative h-[400px] border border-gray-400 bg-gray-200 [&>div]:!h-full">
      {isLoading || !planogram ? (
        <div className="flex size-full items-center justify-center">
          <Loader />
        </div>
      ) : (
        <ReusablePlanogramViewer
          ref={viewerRef}
          enableMinimap
          products={planogram.products ?? []}
          activeProductIds={activeProductIds}
          backgroundImageUrl={planogram.image_url ?? ""}
        >
          {renderConfirmationPlanogramProduct}
        </ReusablePlanogramViewer>
      )}
    </div>
  );
};
